import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const HEADING = { fontFamily: 'Space Grotesk, sans-serif' };

const WHAT_YOU_GET = [
  'AI Acceptable Use Policy',
  'AI Tool Inventory',
  'Vendor Risk Scorecards',
  'AI Incident Log',
  'Employee AI Agreement',
  'Risk Readiness Score',
  'Governance Summary Report',
];

const PROBLEM_POINTS = [
  'No record of AI-driven decisions',
  'No defined responsibility for outputs',
  'No audit trail if something goes wrong',
  'No control over data being entered into tools',
];

const SOLUTION_POINTS = [
  'Define how AI is used',
  'Assign responsibility',
  'Track tools and vendors',
  'Record incidents and risks',
  'Create audit-ready documentation',
];

const RISK_POINTS = [
  'No decision record means no accountability',
  'No output review means no oversight',
  'No audit evidence means exposure in disputes',
  'No data controls means risk of misuse',
];

const AUDIENCE = [
  'Agencies using AI for client work',
  'E-commerce brands using AI tools',
  'Service businesses using AI internally',
  'Teams with multiple employees using AI',
  'Operators preparing for client scrutiny or audits',
];

const SPEED_POINTS = [
  'Fill-in structure',
  'No legal writing required',
  'Works for small teams or growing companies',
  'Can be updated as your AI usage grows',
];

const FAQS = [
  {
    q: 'Is this declaration?',
    a: 'No. This supports Accountability Declared status. Verification requires additional review.',
  },
  {
    q: 'Do I need legal knowledge?',
    a: 'No. This is structured for non-legal users.',
  },
  {
    q: 'How long does it take?',
    a: '30–60 minutes initial setup.',
  },
  {
    q: 'Who is this for?',
    a: 'Any business using AI tools in operations.',
  },
];

export default function Start() {
  const navigate = useNavigate();
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goToPurchase = () => navigate('/assessment/form');
  const scrollToIncludes = () => {
    const el = document.getElementById('what-you-get');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const PrimaryCTA = ({ className = '', testId }) => (
    <Button
      size="lg"
      onClick={goToPurchase}
      className={`rounded-none bg-zinc-900 hover:bg-black text-white px-8 py-6 text-base h-auto ${className}`}
      data-testid={testId}
    >
      Get the AI Governance System for $49
    </Button>
  );

  const SecondaryCTA = ({ className = '', testId }) => (
    <Button
      size="lg"
      variant="outline"
      onClick={scrollToIncludes}
      className={`rounded-none border-zinc-900 text-zinc-900 hover:bg-zinc-100 px-8 py-6 text-base h-auto ${className}`}
      data-testid={testId}
    >
      See what is included
    </Button>
  );

  const Bullet = ({ children }) => (
    <li className="flex items-start gap-3 text-zinc-700">
      <span className="mt-2 block h-1.5 w-1.5 flex-shrink-0 bg-zinc-900" aria-hidden />
      <span className="leading-relaxed">{children}</span>
    </li>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="start-page">
      {/* Minimal Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-zinc-900" style={HEADING}>
            AASC
          </Link>
          <div className="hidden sm:block">
            <Button
              onClick={goToPurchase}
              className="rounded-none bg-zinc-900 hover:bg-black text-white h-auto px-5 py-2.5 text-sm"
              data-testid="header-cta-btn"
            >
              Get the System — $49
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* SECTION 1: HERO */}
        <section className="px-6 py-20 sm:py-28 border-b border-zinc-200">
          <div className="container mx-auto max-w-4xl">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">
              AI Governance Documentation System
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-zinc-900 tracking-tight leading-[1.05] mb-6"
              style={HEADING}
              data-testid="hero-headline"
            >
              Document how your business uses AI before it creates risk you cannot defend.
            </h1>
            <p className="text-lg sm:text-xl text-zinc-600 max-w-2xl leading-relaxed mb-10">
              A structured, audit-ready AI governance system you can complete in 30–60 minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 mb-6">
              <PrimaryCTA testId="hero-primary-cta" />
              <SecondaryCTA testId="hero-secondary-cta" />
            </div>
            <p className="text-sm text-zinc-500">
              Used by agencies, operators, and teams using AI in real workflows.
            </p>
          </div>
        </section>

        {/* SECTION 2: PROBLEM */}
        <section className="px-6 py-20 border-b border-zinc-200 bg-zinc-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-10" style={HEADING}>
              Most AI use is undocumented.
            </h2>
            <ul className="space-y-4 mb-10">
              {PROBLEM_POINTS.map((p) => (
                <Bullet key={p}>{p}</Bullet>
              ))}
            </ul>
            <div className="border-l-2 border-zinc-900 pl-6">
              <p className="text-xl text-zinc-900 font-medium" style={HEADING}>
                If it is not documented, it cannot be defended.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUTION */}
        <section className="px-6 py-20 border-b border-zinc-200">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-6" style={HEADING}>
              This system gives you proof.
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mb-10">
              This product creates a structured, documented record of how AI is used in your
              business. It replaces guesswork with defined policies, ownership, and evidence.
            </p>
            <ul className="space-y-4 max-w-2xl">
              {SOLUTION_POINTS.map((p) => (
                <Bullet key={p}>{p}</Bullet>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 4: WHAT YOU GET */}
        <section id="what-you-get" className="px-6 py-20 border-b border-zinc-200 bg-zinc-900 text-white">
          <div className="container mx-auto max-w-5xl">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">
              What you get
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-12" style={HEADING}>
              Complete AI Governance Documentation System
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 mb-10">
              {WHAT_YOU_GET.map((item, idx) => (
                <div
                  key={item}
                  className="bg-zinc-900 p-6 flex items-start gap-4"
                  data-testid={`include-item-${idx}`}
                >
                  <span className="text-zinc-500 font-mono text-sm pt-1">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="text-white font-medium leading-snug">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-zinc-400 max-w-2xl">
              All structured and ready to use. No legal or compliance background required.
            </p>
          </div>
        </section>

        {/* SECTION 5: RISK */}
        <section className="px-6 py-20 border-b border-zinc-200">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-10" style={HEADING}>
              What happens if you do nothing.
            </h2>
            <ul className="space-y-4 mb-10">
              {RISK_POINTS.map((p) => (
                <Bullet key={p}>{p}</Bullet>
              ))}
            </ul>
            <div className="border-l-2 border-zinc-900 pl-6">
              <p className="text-xl text-zinc-900 font-medium" style={HEADING}>
                Most failures are not technical. They are undocumented.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 6: WHO THIS IS FOR */}
        <section className="px-6 py-20 border-b border-zinc-200 bg-zinc-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-10" style={HEADING}>
              Who this is for.
            </h2>
            <ul className="space-y-4">
              {AUDIENCE.map((p) => (
                <Bullet key={p}>{p}</Bullet>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 7: POSITIONING */}
        <section className="px-6 py-20 border-b border-zinc-200">
          <div className="container mx-auto max-w-5xl">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Where this fits in AASC
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-12" style={HEADING}>
              The AASC progression.
            </h2>
            <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              <div className="bg-white p-8 relative" data-testid="stage-alignment">
                <div className="absolute top-0 left-0 h-1 w-full bg-zinc-900" />
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3">Stage 1</p>
                <h3 className="text-xl font-semibold text-zinc-900 mb-3" style={HEADING}>
                  Alignment Declared
                </h3>
                <p className="text-zinc-600 leading-relaxed mb-4">
                  You document your system.
                </p>
                <span className="inline-block border border-zinc-900 text-zinc-900 text-xs px-3 py-1 font-medium">
                  This product gets you here
                </span>
              </div>
              <div className="bg-white p-8">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3">Stage 2</p>
                <h3 className="text-xl font-semibold text-zinc-900 mb-3" style={HEADING}>
                  Verified
                </h3>
                <p className="text-zinc-600 leading-relaxed">
                  Documentation and evidence reviewed.
                </p>
              </div>
              <div className="bg-white p-8">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3">Stage 3</p>
                <h3 className="text-xl font-semibold text-zinc-900 mb-3" style={HEADING}>
                  Declared
                </h3>
                <p className="text-zinc-600 leading-relaxed">
                  Controls tested and enforced.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: SPEED + EASE */}
        <section className="px-6 py-20 border-b border-zinc-200 bg-zinc-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-10" style={HEADING}>
              Complete in under 60 minutes.
            </h2>
            <ul className="space-y-4">
              {SPEED_POINTS.map((p) => (
                <Bullet key={p}>{p}</Bullet>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 9: PRICING */}
        <section className="px-6 py-24 border-b border-zinc-200">
          <div className="container mx-auto max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-12" style={HEADING}>
              Simple, one-time access.
            </h2>

            <div className="border border-zinc-900 p-8 sm:p-10">
              <div className="flex items-baseline gap-3 mb-8">
                <span className="text-6xl font-semibold text-zinc-900" style={HEADING}>
                  $49
                </span>
                <span className="text-zinc-500">one-time</span>
              </div>
              <ul className="space-y-3 mb-8 text-zinc-700">
                <li className="flex items-center gap-3">
                  <span className="h-px w-6 bg-zinc-900" />
                  Instant download
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-px w-6 bg-zinc-900" />
                  Use internally or with clients
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-px w-6 bg-zinc-900" />
                  Keep and reuse
                </li>
              </ul>
              <PrimaryCTA className="w-full sm:w-auto" testId="pricing-cta-btn" />
            </div>
          </div>
        </section>

        {/* SECTION 10: FINAL CTA */}
        <section className="px-6 py-24 bg-zinc-900 text-white">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl sm:text-4xl font-semibold mb-6" style={HEADING}>
              Start documenting your AI system.
            </h2>
            <p className="text-lg text-zinc-400 mb-10 max-w-xl">
              Do not wait until something goes wrong to figure this out.
            </p>
            <Button
              size="lg"
              onClick={goToPurchase}
              className="rounded-none bg-white hover:bg-zinc-100 text-zinc-900 px-8 py-6 text-base h-auto"
              data-testid="final-cta-btn"
            >
              Get the AI Governance System for $49
            </Button>
          </div>
        </section>

        {/* SECTION 11: FAQ */}
        <section className="px-6 py-24 border-t border-zinc-200">
          <div className="container mx-auto max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-12" style={HEADING}>
              Questions.
            </h2>
            <Accordion type="single" collapsible className="border-t border-zinc-200">
              {FAQS.map((faq, idx) => (
                <AccordionItem
                  key={faq.q}
                  value={`faq-${idx}`}
                  className="border-b border-zinc-200"
                  data-testid={`faq-item-${idx}`}
                >
                  <AccordionTrigger className="text-left text-lg font-medium text-zinc-900 py-6 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-600 leading-relaxed pb-6">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </main>

      {/* Minimal Footer */}
      <footer className="py-10 px-6 border-t border-zinc-200 bg-white">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span>&copy; {new Date().getFullYear()} AI Accountability Standards Council</span>
          <div className="flex items-center gap-6">
            <Link to="/registry" className="hover:text-zinc-900">Registry</Link>
            <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
            <Link to="/contact" className="hover:text-zinc-900">Contact</Link>
          </div>
        </div>
      </footer>

      {/* Sticky CTA bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-200 bg-white/95 backdrop-blur transition-transform duration-300 ${
          showSticky ? 'translate-y-0' : 'translate-y-full'
        }`}
        data-testid="sticky-cta-bar"
      >
        <div className="container mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-zinc-900">AI Governance Documentation System</p>
            <p className="text-xs text-zinc-500">$49 one-time · Instant download</p>
          </div>
          <Button
            onClick={goToPurchase}
            className="rounded-none bg-zinc-900 hover:bg-black text-white h-auto px-6 py-3 text-sm w-full sm:w-auto"
            data-testid="sticky-cta-btn"
          >
            Get the AI Governance System for $49
          </Button>
        </div>
      </div>
    </div>
  );
}
